import { FC, useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Switch,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useStyles from "./styles";
import {
  settingsToForm,
  useGetBillingSettings,
  useUpdateBillingSettings,
} from "../../api/billingAdmin";

/**
 * Trial and promo switches.
 *
 * These used to live in STRIPE_TRIAL_DAYS in the API's .env, so changing the
 * free-trial length meant a redeploy and a container restart.
 */
export const BillingSettingsTab: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { settings, isLoading } = useGetBillingSettings();
  const updateSettings = useUpdateBillingSettings();

  const [trialEnabled, setTrialEnabled] = useState(true);
  const [trialDays, setTrialDays] = useState(14);
  const [promoEnabled, setPromoEnabled] = useState(true);
  const [dirty, setDirty] = useState(false);

  // Hydrate once the API responds. Guarded on `dirty` so a background refetch
  // cannot discard edits the user has already made but not yet saved.
  useEffect(() => {
    if (!settings.length || dirty) return;
    const form = settingsToForm(settings);
    setTrialEnabled(form.trial_enabled);
    setTrialDays(form.trial_days);
    setPromoEnabled(form.promo_enabled);
  }, [settings, dirty]);

  const daysError =
    trialEnabled && (!Number.isFinite(trialDays) || trialDays < 1 || trialDays > 365);

  const handleSave = () => {
    if (daysError) return;
    updateSettings.mutate(
      {
        trial_enabled: trialEnabled,
        trial_days: trialDays,
        promo_enabled: promoEnabled,
      },
      { onSuccess: () => setDirty(false) }
    );
  };

  const handleReset = () => {
    const form = settingsToForm(settings);
    setTrialEnabled(form.trial_enabled);
    setTrialDays(form.trial_days);
    setPromoEnabled(form.promo_enabled);
    setDirty(false);
  };

  if (isLoading) {
    return (
      <div className={classes.card}>
        <div className={classes.emptyState}>
          <CircularProgress size={28} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={classes.card}>
        <div className={classes.cardTitle}>
          <h3>{t("billing.trialSection", "Période d'essai")}</h3>
        </div>
        <p className={classes.cardHint}>
          {t(
            "billing.trialHint",
            "S'applique aux nouveaux abonnements uniquement. Les périodes d'essai déjà en cours ne sont pas interrompues."
          )}
        </p>

        <div className={classes.settingRow}>
          <div className={classes.settingLabel}>
            <strong>{t("billing.trialEnabled", "Essai gratuit activé")}</strong>
            <span>
              {t(
                "billing.trialEnabledDesc",
                "Lorsque cette option est désactivée, les nouveaux clients sont facturés dès la souscription."
              )}
            </span>
          </div>
          <div className={classes.settingControl}>
            <Switch
              checked={trialEnabled}
              onChange={(e) => {
                setTrialEnabled(e.target.checked);
                setDirty(true);
              }}
              inputProps={{
                "aria-label": t("billing.trialEnabled", "Essai gratuit activé"),
              }}
            />
          </div>
        </div>

        <div className={classes.settingRow}>
          <div className={classes.settingLabel}>
            <strong>{t("billing.trialDays", "Durée de l'essai (jours)")}</strong>
            <span>
              {t(
                "billing.trialDaysDesc",
                "Entre 1 et 365 jours. La valeur standard est de 14 jours."
              )}
            </span>
          </div>
          {/* Dimmed rather than hidden: the value stays visible so an admin can
              see what will resume if the trial is switched back on. */}
          <div
            className={`${classes.settingControl} ${
              trialEnabled ? "" : classes.dimmed
            }`}
          >
            <TextField
              type="number"
              size="small"
              value={trialDays}
              onChange={(e) => {
                setTrialDays(parseInt(e.target.value, 10));
                setDirty(true);
              }}
              error={daysError}
              helperText={
                daysError
                  ? t("billing.trialDaysError", "Entre 1 et 365")
                  : undefined
              }
              inputProps={{ min: 1, max: 365, "aria-label": "days" }}
              sx={{ width: 120 }}
            />
          </div>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>
          <h3>{t("billing.promoSection", "Codes promo")}</h3>
        </div>
        <p className={classes.cardHint}>
          {t(
            "billing.promoHint",
            "Contrôle le champ « code promo » affiché au moment du paiement."
          )}
        </p>

        <div className={classes.settingRow}>
          <div className={classes.settingLabel}>
            <strong>
              {t("billing.promoEnabled", "Codes promo acceptés")}
            </strong>
            <span>
              {t(
                "billing.promoEnabledDesc",
                "Désactiver masque le champ au paiement. Les remises déjà appliquées à un abonnement continuent de courir."
              )}
            </span>
          </div>
          <div className={classes.settingControl}>
            <Switch
              checked={promoEnabled}
              onChange={(e) => {
                setPromoEnabled(e.target.checked);
                setDirty(true);
              }}
              inputProps={{
                "aria-label": t("billing.promoEnabled", "Codes promo acceptés"),
              }}
            />
          </div>
        </div>
      </div>

      <div className={classes.actionsRow}>
        <Button
          variant="text"
          onClick={handleReset}
          disabled={!dirty || updateSettings.isPending}
        >
          {t("common.cancel", "Annuler")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!dirty || daysError || updateSettings.isPending}
          startIcon={
            updateSettings.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {t("common.save", "Enregistrer")}
        </Button>
      </div>
    </>
  );
};

export default BillingSettingsTab;
